import { Fragment } from "react"
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router"
import { Sun, Moon, Monitor } from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { ArrowLeft } from "@/components/icons"
import { TopBarBackButton } from "@/components/topbar/TopBarBackButton"
import { AppLayout } from "@/components/AppLayout"
import { DesktopPage } from "@/components/desktop/DesktopPage"
import { DesktopPageHeader } from "@/components/desktop/DesktopPageHeader"
import { useIsDesktop } from "@/components/ui/use-mobile"
import { Card } from "@/components/ui/card"
import { MenuItem } from "@/components/ui/menu-item"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { useTheme } from "@/components/theme-provider"

export const Route = createFileRoute("/_auth/profile/settings/appearance")({
  component: RouteComponent,
})

const SCREEN_MODE_OPTIONS: Array<{
  value: "light" | "dark" | "system"
  label: string
  icon: LucideIcon
}> = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
]

function RouteComponent() {
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()
  const { theme, setTheme } = useTheme()

  const body = (
    <div className="flex flex-col gap-6">
        <div>
          <p className="text-sm font-semibold text-content-primary-bold mb-4 px-1">
            Screen Mode
          </p>
          <Card className="flex flex-col gap-2 w-full px-2">
            <RadioGroup
              value={theme}
              onValueChange={setTheme}
              className="flex flex-col gap-2"
            >
              {SCREEN_MODE_OPTIONS.map((option, index) => {
                const Icon = option.icon
                return (
                  <Fragment key={option.value}>
                    {index > 0 && <hr className="border-border-L1" />}
                    <MenuItem
                      leftIcon={<Icon className="size-5 text-content-primary-bold" />}
                      rightIcon={<RadioGroupItem value={option.value} />}
                      size="large"
                      onClick={() => setTheme(option.value)}
                    >
                      {option.label}
                    </MenuItem>
                  </Fragment>
                )
              })}
            </RadioGroup>
          </Card>
        </div>
      </div>
  )

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader
          title="Appearance"
          breadcrumb={
            <span className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate({ to: "/profile/settings" })}
                className="flex size-7 items-center justify-center rounded-full hover:bg-surface-L1"
                aria-label="Back to settings"
              >
                <ArrowLeft className="size-4" />
              </button>
              <span>
                <Link to="/profile" className="hover:underline">
                  Profile
                </Link>
                <span className="text-content-primary-subtle"> / </span>
                <Link to="/profile/settings" className="hover:underline">
                  Settings
                </Link>
                <span className="text-content-primary-subtle"> / Appearance</span>
              </span>
            </span>
          }
        />
        {body}
      </DesktopPage>
    )
  }

  return (
    <AppLayout topBar={<TopBarBackButton title="Appearance" />}>
      {body}
    </AppLayout>
  )
}
